import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  useLoadTechAuditQuery,
  useUpsertTechCategoryMutation,
  useUpsertTechEntryMutation,
  useAddTechTaskMutation,
  useUpdateTechTaskMutation,
  useDeleteTechTaskMutation,
  useDeleteTechCategoryMutation,
  useListFirmsQuery,
} from '../app/api';
import FirmsSection from './FirmsSection';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
};

// The company columns are no longer hard-coded — they're driven by the `firms`
// lookup (see `companyColumns` inside the component). Audit entries are keyed by
// the firm *name* string, so adding a firm just adds a column and existing
// rows keep matching.

const AUDIT_STATUSES = {
  'TBD':         '#9d9fa1',
  'In Progress': '#0069a6',
  'Completed':   '#2e7d32',   // green
  'Blocked':     '#c62828',
};

// Any status not in the current option set (e.g. legacy "Keep"/"Replace"/
// "Evaluate"/"Consolidated") falls back to TBD for display.
const normalizeAuditStatus = (s) => (s in AUDIT_STATUSES ? s : 'TBD');

// Colors for the per-firm status boxes in each category header.
// TBD is intentionally absent → renders as the faint "no status" box.
const FIRM_BOX_COLORS = {
  'In Progress': '#d09b2c',  // yellow
  'Completed':   '#2e7d32',  // green
  'Blocked':     '#c62828',  // red
};

const TIMELINE_PHASES = ['Planning', 'Data Migration', 'Training', 'Testing', 'Go Live', 'Other'];

const TIMELINE_STATUSES = {
  'Not Started': '#9d9fa1',
  'In Progress': '#0069a6',
  'Complete':    '#2e7d32',
  'Blocked':     '#c62828',
  'At Risk':     '#d09b2c',
};

const DEFAULT_CATEGORIES = [
  { id: 'email',        name: 'Email & Communication',       icon: 'envelope'         },
  { id: 'messaging',    name: 'Messaging & Chat',            icon: 'chat-dots'        },
  { id: 'video',        name: 'Video Conferencing',          icon: 'camera-video'     },
  { id: 'phone',        name: 'Phone / VoIP',                icon: 'telephone'        },
  { id: 'vpn',          name: 'VPN / Remote Access',         icon: 'shield-lock'      },
  { id: 'crm',          name: 'CRM',                         icon: 'people'           },
  { id: 'financial',    name: 'Financial Planning Software', icon: 'calculator'       },
  { id: 'portfolio',    name: 'Portfolio Management',        icon: 'bar-chart-line'   },
  { id: 'trading',      name: 'Trading & Execution',         icon: 'graph-up-arrow'   },
  { id: 'compliance',   name: 'Compliance & Regulatory',     icon: 'shield-check'     },
  { id: 'documents',    name: 'Document Management',         icon: 'folder2-open'     },
  { id: 'esignature',   name: 'E-Signature',                 icon: 'pen'              },
  { id: 'hr',           name: 'HR & Payroll',                icon: 'person-badge'     },
  { id: 'security',     name: 'Cybersecurity / Endpoint',    icon: 'bug-fill'         },
  { id: 'backup',       name: 'Backup & Disaster Recovery',  icon: 'cloud-arrow-up'   },
  { id: 'network',      name: 'Network Infrastructure',      icon: 'diagram-3'        },
  { id: 'storage',      name: 'Cloud Storage',               icon: 'hdd-network'      },
  { id: 'mfa',          name: 'MFA / Identity Management',   icon: 'key'              },
  { id: 'accounting',   name: 'Accounting & Billing',        icon: 'receipt'          },
];

const blankEntry = () => ({ vendor: '', status: 'TBD', cost: '', renewal: '', contact: '', notes: '' });

// Per-category company data starts empty; entries are filled in from the API on
// hydration and the table renders one column per firm, falling back to a blank
// entry for any (firm, category) pair that has no row yet.
const buildInitialData = () =>
  Object.fromEntries(
    DEFAULT_CATEGORIES.map(cat => [
      cat.id,
      {
        selectedVendor: '',
        companies: {},
        timeline: [],
      },
    ])
  );

// ── Styles ───────────────────────────────────────────────────────────────────

const cellInput = {
  width: '100%', padding: '5px 8px', border: `1px solid ${brand.softGray}`,
  borderRadius: '4px', fontSize: '0.815rem', color: brand.deepBlue,
  outline: 'none', fontFamily: 'inherit', background: 'white',
};

const thStyle = {
  padding: '0.55rem 0.75rem', color: brand.charcoal, fontWeight: 700,
  fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px',
  textAlign: 'left', whiteSpace: 'nowrap', background: 'rgba(10,41,71,0.03)',
};

// ── Notes cell ───────────────────────────────────────────────────────────────

// Notes are stored as TEXT, so they can hold far more than fits on one table
// row. The cell renders as a one-line textarea by default and expands to a
// multi-line, resizable box on demand — newlines and long entries are kept
// intact either way (a textarea, unlike an input, never strips them).
const notesToggle = {
  border: 'none', background: 'transparent', padding: '2px 1px',
  lineHeight: 1, cursor: 'pointer', flexShrink: 0,
};

function NotesCell({ value, onChange, onSave }) {
  const [expanded, setExpanded] = useState(false);
  const text = value || '';
  // Collapsed rows that are hiding something get a goldenrod toggle so you can
  // tell at a glance which notes have more behind them.
  const hasMore = text.includes('\n') || text.length > 30;

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
      <textarea
        rows={expanded ? 6 : 1}
        value={text}
        placeholder="Notes…"
        title={!expanded && hasMore ? text : undefined}
        onChange={e => onChange(e.target.value)}
        onBlur={onSave}
        style={{
          ...cellInput,
          resize: expanded ? 'vertical' : 'none',
          overflow: expanded ? 'auto' : 'hidden',
          whiteSpace: expanded ? 'pre-wrap' : 'nowrap',
          lineHeight: '1.35',
        }}
      />
      <button type="button" style={notesToggle}
        title={expanded ? 'Collapse notes' : 'Expand notes'}
        aria-label={expanded ? 'Collapse notes' : 'Expand notes'}
        aria-expanded={expanded}
        onClick={() => setExpanded(x => !x)}>
        <i className={`bi bi-arrows-angle-${expanded ? 'contract' : 'expand'}`}
          style={{
            fontSize: '0.7rem',
            color: expanded ? brand.tealBlue : (hasMore ? brand.goldenrod : brand.charcoal),
          }} />
      </button>
    </div>
  );
}

NotesCell.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

// ── Gantt ─────────────────────────────────────────────────────────────────────

function GanttChart({ tasks }) {
  const dated = tasks.filter(t => t.startDate && t.endDate);
  if (dated.length === 0) return null;

  const toMs  = d => new Date(d).getTime();
  const allMs = dated.flatMap(t => [toMs(t.startDate), toMs(t.endDate)]);
  const minT  = Math.min(...allMs);
  const maxT  = Math.max(...allMs);
  const range = maxT - minT || 1;

  const labelDates = [];
  const d = new Date(minT);
  d.setDate(1);
  while (d.getTime() <= maxT) {
    labelDates.push(new Date(d));
    d.setMonth(d.getMonth() + 1);
  }

  return (
    <div style={{ padding: '0.75rem 0 0.25rem' }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: brand.charcoal, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>
        Gantt View
      </div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
        <div style={{ flexShrink: 0, width: '140px' }}>
          <div style={{ height: '20px' }} />
          {dated.map(t => (
            <div key={t.id} style={{ height: '26px', display: 'flex', alignItems: 'center', fontSize: '0.73rem', color: brand.charcoal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {t.task || t.type}
            </div>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
          <div style={{ position: 'relative', height: '20px', marginBottom: '2px' }}>
            {labelDates.map(ld => {
              const pct = ((ld.getTime() - minT) / range) * 100;
              return (
                <span key={ld.getTime()} style={{ position: 'absolute', left: `${pct}%`, fontSize: '0.65rem', color: brand.charcoal, whiteSpace: 'nowrap', transform: 'translateX(-50%)' }}>
                  {ld.toLocaleString('default', { month: 'short', year: '2-digit' })}
                </span>
              );
            })}
          </div>
          {dated.map(t => {
            const left  = ((toMs(t.startDate) - minT) / range) * 100;
            const width = Math.max(((toMs(t.endDate) - toMs(t.startDate)) / range) * 100, 1.5);
            const color = TIMELINE_STATUSES[t.status] || '#9d9fa1';
            return (
              <div key={t.id} style={{ position: 'relative', height: '26px', background: `${brand.softGray}88`, borderRadius: '3px', marginBottom: '2px' }}>
                <div style={{ position: 'absolute', left: `${left}%`, width: `${width}%`, height: '100%', background: color, borderRadius: '3px', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                  <span style={{ fontSize: '0.65rem', color: 'white', fontWeight: 600, padding: '0 6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {t.type}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
GanttChart.propTypes = { tasks: PropTypes.array };

// ── Main Component ────────────────────────────────────────────────────────────

export default function TechIntegration() {
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [data, setData]             = useState(buildInitialData);
  const [collapsed, setCollapsed]   = useState(() =>
    Object.fromEntries(DEFAULT_CATEGORIES.map(c => [c.id, true]))
  );
  const [showAddCat, setShowAddCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');

  // ── API hooks ────────────────────────────────────────────────────────────────

  const { data: auditData, isLoading } = useLoadTechAuditQuery();
  const { data: firms = [] } = useListFirmsQuery();
  const [triggerUpsertCategory] = useUpsertTechCategoryMutation();
  const [triggerUpsertEntry]    = useUpsertTechEntryMutation();
  const [triggerAddTask]        = useAddTechTaskMutation();
  const [triggerUpdateTask]     = useUpdateTechTaskMutation();
  const [triggerDeleteTask]     = useDeleteTechTaskMutation();
  const [triggerDeleteCategory] = useDeleteTechCategoryMutation();

  // Inline-edit state for category names. When `editingCategoryId` is set,
  // that category's header renders an <input> bound to `editingCategoryName`.
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');

  // ── Company columns (firm-driven) ─────────────────────────────────────────────
  // Columns come from the firms lookup, in firm order. We also append any
  // company that already has audit entries but isn't in the firms table — so
  // historical data is never hidden (e.g. if a firm was renamed/removed, its
  // column stays until those rows are cleared, and re-adding the firm reunites
  // it with its data).
  const companyColumns = useMemo(() => {
    const fromFirms = firms.map(f => f.name);
    const seen = new Set(fromFirms.map(n => n.toLowerCase()));
    const extras = [];
    for (const e of (auditData?.entries || [])) {
      const c = (e.company || '').trim();
      if (c && !seen.has(c.toLowerCase())) {
        seen.add(c.toLowerCase());
        extras.push(c);
      }
    }
    return [...fromFirms, ...extras];
  }, [firms, auditData]);

  // ── Hydrate state from API ────────────────────────────────────────────────────

  useEffect(() => {
    if (!auditData) return;

    const merged = buildInitialData();
    const extraCats = [];

    // For default categories already in `merged`, track any name/icon override
    // the DB has stored so we can update the in-memory `categories` array too.
    const defaultOverrides = {};
    auditData.categories.forEach(cat => {
      if (merged[cat.category_key]) {
        merged[cat.category_key].selectedVendor = cat.selected_vendor || '';
        if (cat.name) defaultOverrides[cat.category_key] = { name: cat.name, icon: cat.icon || undefined };
      } else {
        merged[cat.category_key] = {
          selectedVendor: cat.selected_vendor || '',
          companies: {},
          timeline: [],
        };
        extraCats.push({ id: cat.category_key, name: cat.name, icon: cat.icon || 'tag' });
      }
    });

    // Fill in every entry by its (category, company) key. We key on the company
    // name as stored, so data shows whether or not that firm is still in the
    // firms lookup — nothing entered is ever dropped here.
    auditData.entries.forEach(e => {
      if (merged[e.category_key]) {
        merged[e.category_key].companies[e.company] = {
          vendor:  e.vendor  || '',
          status:  normalizeAuditStatus(e.status || 'TBD'),
          cost:    e.cost    || '',
          renewal: e.renewal_date || '',
          contact: e.contact || '',
          notes:   e.notes   || '',
        };
      }
    });

    auditData.timeline.forEach(t => {
      if (merged[t.category_key]) {
        merged[t.category_key].timeline.push({
          id:        t.id,
          task:      t.task      || '',
          type:      t.phase     || 'Planning',
          owner:     t.owner     || '',
          startDate: t.start_date || '',
          endDate:   t.end_date   || '',
          status:    t.status    || 'Not Started',
          notes:     t.notes     || '',
        });
      }
    });

    setData(merged);
    setCategories(prev => {
      const withOverrides = prev.map(c =>
        defaultOverrides[c.id]
          ? { ...c, name: defaultOverrides[c.id].name, icon: defaultOverrides[c.id].icon || c.icon }
          : c
      );
      const existingIds = new Set(withOverrides.map(c => c.id));
      const additions = extraCats.filter(c => !existingIds.has(c.id));
      return [...withOverrides, ...additions];
    });
    if (extraCats.length > 0) {
      setCollapsed(prev => ({
        ...prev,
        ...Object.fromEntries(extraCats.map(c => [c.id, true])),
      }));
    }
  }, [auditData]);

  // ── Local state helpers ───────────────────────────────────────────────────────

  const updateCompany = (catId, co, field, value) =>
    setData(prev => ({
      ...prev,
      [catId]: { ...prev[catId], companies: { ...prev[catId].companies, [co]: { ...prev[catId].companies[co], [field]: value } } },
    }));

  const updateMeta = (catId, field, value) =>
    setData(prev => ({ ...prev, [catId]: { ...prev[catId], [field]: value } }));

  const updateTask = (catId, taskId, field, value) =>
    setData(prev => ({
      ...prev,
      [catId]: { ...prev[catId], timeline: prev[catId].timeline.map(t => t.id === taskId ? { ...t, [field]: value } : t) },
    }));

  // ── Save helpers (call API) ───────────────────────────────────────────────────

  const saveEntry = useCallback((catId, co, overrides = {}) => {
    setData(prev => {
      const entry = { ...prev[catId].companies[co], ...overrides };
      triggerUpsertEntry({
        category_key: catId, company: co,
        vendor: entry.vendor, status: entry.status, cost: entry.cost,
        renewal_date: entry.renewal || null, contact: entry.contact, notes: entry.notes,
      });
      return prev;
    });
  }, [triggerUpsertEntry]);

  const saveSelectedVendor = useCallback((catId, vendor) => {
    // For custom categories, look up the user-entered name from the live
    // `categories` state — DEFAULT_CATEGORIES won't have it, and falling
    // back to `catId` would overwrite the stored name with the custom_*
    // identifier on every vendor save.
    const cat = DEFAULT_CATEGORIES.find(c => c.id === catId)
             || categories.find(c => c.id === catId)
             || { name: catId, icon: 'tag' };
    triggerUpsertCategory({
      category_key: catId, name: cat.name, icon: cat.icon || 'tag',
      selected_vendor: vendor,
    });
  }, [triggerUpsertCategory, categories]);

  const saveTask = useCallback((catId, taskId) => {
    setData(prev => {
      const task = prev[catId]?.timeline.find(t => t.id === taskId);
      if (task) {
        triggerUpdateTask({
          id: taskId, task: task.task, phase: task.type, owner: task.owner,
          start_date: task.startDate || null, end_date: task.endDate || null,
          status: task.status, notes: task.notes,
        });
      }
      return prev;
    });
  }, [triggerUpdateTask]);

  // ── Status selects save immediately on change ────────────────────────────────

  const handleStatusChange = (catId, co, value) => {
    updateCompany(catId, co, 'status', value);
    saveEntry(catId, co, { status: value });
  };

  const handleTaskStatusChange = (catId, taskId, value) => {
    updateTask(catId, taskId, 'status', value);
    setData(prev => {
      const task = prev[catId]?.timeline.find(t => t.id === taskId);
      if (task) {
        triggerUpdateTask({
          id: taskId, task: task.task, phase: task.type, owner: task.owner,
          start_date: task.startDate || null, end_date: task.endDate || null,
          status: value, notes: task.notes,
        });
      }
      return prev;
    });
  };

  // ── Timeline task CRUD ────────────────────────────────────────────────────────

  const addTask = async (catId) => {
    try {
      const result = await triggerAddTask({ category_key: catId }).unwrap();
      setData(prev => ({
        ...prev,
        [catId]: {
          ...prev[catId],
          timeline: [...prev[catId].timeline, {
            id: result.id, task: '', type: 'Planning', owner: '',
            startDate: '', endDate: '', status: 'Not Started', notes: '',
          }],
        },
      }));
    } catch (err) {
      console.error('Failed to add task', err);
    }
  };

  const removeTask = async (catId, taskId) => {
    try {
      await triggerDeleteTask({ id: taskId }).unwrap();
      setData(prev => ({
        ...prev,
        [catId]: { ...prev[catId], timeline: prev[catId].timeline.filter(t => t.id !== taskId) },
      }));
    } catch (err) {
      console.error('Failed to delete task', err);
    }
  };

  // ── Collapse helpers ──────────────────────────────────────────────────────────

  const toggleCollapse = (catId) =>
    setCollapsed(prev => ({ ...prev, [catId]: !prev[catId] }));

  const collapseAll = () =>
    setCollapsed(Object.fromEntries(categories.map(c => [c.id, true])));

  const expandAll = () =>
    setCollapsed(Object.fromEntries(categories.map(c => [c.id, false])));

  // ── Custom category ───────────────────────────────────────────────────────────

  const startRenameCategory = (cat) => {
    setEditingCategoryId(cat.id);
    setEditingCategoryName(cat.name);
  };

  const cancelRenameCategory = () => {
    setEditingCategoryId(null);
    setEditingCategoryName('');
  };

  const saveRenameCategory = async () => {
    const id = editingCategoryId;
    const trimmed = editingCategoryName.trim();
    if (!id || !trimmed) {
      cancelRenameCategory();
      return;
    }
    const cat = categories.find(c => c.id === id);
    if (!cat || trimmed === cat.name) {
      cancelRenameCategory();
      return;
    }
    setCategories(prev => prev.map(c => c.id === id ? { ...c, name: trimmed } : c));
    setEditingCategoryId(null);
    setEditingCategoryName('');
    await triggerUpsertCategory({
      category_key: id,
      name: trimmed,
      icon: cat.icon || 'tag',
      selected_vendor: data[id]?.selectedVendor || null,
    });
  };

  const removeCategory = async (cat) => {
    if (!window.confirm(`Hide the "${cat.name}" category? Its data stays in the database but won't show on this page.`)) return;
    setCategories(prev => prev.filter(c => c.id !== cat.id));
    setData(prev => {
      const next = { ...prev };
      delete next[cat.id];
      return next;
    });
    if (editingCategoryId === cat.id) cancelRenameCategory();
    await triggerDeleteCategory({ category_key: cat.id });
  };

  const addCategory = async () => {
    if (!newCatName.trim()) return;
    const id  = `custom_${Date.now()}`;
    const cat = { id, name: newCatName.trim(), icon: 'tag' };
    setCategories(prev => [...prev, cat]);
    setData(prev => ({
      ...prev,
      [id]: { selectedVendor: '', companies: {}, timeline: [] },
    }));
    setCollapsed(prev => ({ ...prev, [id]: false }));
    setNewCatName('');
    setShowAddCat(false);
    await triggerUpsertCategory({ category_key: id, name: cat.name, icon: cat.icon, selected_vendor: null });
  };

  // ── Summary stats ─────────────────────────────────────────────────────────────

  const allEntries   = categories.flatMap(cat => companyColumns.map(co => data[cat.id]?.companies?.[co]));
  const filledCount  = allEntries.filter(e => e?.vendor?.trim()).length;
  const totalCount   = allEntries.length;
  const statusCounts = Object.fromEntries(
    Object.keys(AUDIT_STATUSES).map(s => [s, allEntries.filter(e => e?.status === s).length])
  );

  // ── Button styles ─────────────────────────────────────────────────────────────

  const btnPrimary = { background: brand.deepBlue, color: 'white', border: 'none', borderRadius: '6px', padding: '7px 14px', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' };
  const btnOutline = { background: 'transparent', color: brand.tealBlue, border: `1px solid ${brand.tealBlue}`, borderRadius: '6px', padding: '6px 12px', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer' };
  const btnSmall   = { background: 'transparent', color: brand.tealBlue, border: `1px solid ${brand.tealBlue}`, borderRadius: '5px', padding: '4px 10px', fontWeight: 600, fontSize: '0.74rem', cursor: 'pointer' };
  const btnDanger  = { background: 'transparent', color: '#c62828', border: '1px solid #c62828', borderRadius: '4px', padding: '3px 8px', fontWeight: 600, fontSize: '0.72rem', cursor: 'pointer' };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem' }}>

      {/* Page header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h4 style={{ color: brand.deepBlue, fontWeight: 800, margin: '0 0 2px' }}>Tech Audit — Integration Planning</h4>
          <p style={{ color: brand.charcoal, margin: 0, fontSize: '0.875rem' }}>
            Technology consolidation across C2P · JLS · AWM · FSC · OTF · Hammer → Prosperity Capital Advisors 
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {isLoading && <span style={{ fontSize: '0.78rem', color: brand.charcoal }}><i className="bi bi-arrow-repeat me-1" />Loading…</span>}
          <button style={btnOutline} onClick={expandAll}>Expand All</button>
          <button style={btnOutline} onClick={collapseAll}>Collapse All</button>
          <button style={btnPrimary} onClick={() => setShowAddCat(v => !v)}>
            <i className="bi bi-plus me-1" />{showAddCat ? 'Cancel' : 'Add Category'}
          </button>
        </div>
      </div>

      {/* Summary bar */}
      <div style={{ background: 'white', borderRadius: '10px', padding: '0.875rem 1.25rem', marginBottom: '1.25rem', boxShadow: '0 2px 8px rgba(10,41,71,0.08)', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '1.25rem' }}>
        <div style={{ marginRight: '0.25rem' }}>
          <span style={{ fontWeight: 800, color: brand.deepBlue, fontSize: '1.4rem' }}>{filledCount}</span>
          <span style={{ color: brand.charcoal, fontSize: '0.8rem', marginLeft: '4px' }}>/ {totalCount} entries filled</span>
        </div>
        <div style={{ width: '1px', height: '24px', background: brand.softGray }} />
        {Object.entries(AUDIT_STATUSES).map(([status, color]) => (
          <div key={status} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '9px', height: '9px', borderRadius: '50%', background: color, flexShrink: 0 }} />
            <span style={{ fontSize: '0.8rem', color: brand.charcoal }}>
              {status}: <strong style={{ color: brand.deepBlue }}>{statusCounts[status]}</strong>
            </span>
          </div>
        ))}
      </div>

      {/* Add category form */}
      {showAddCat && (
        <div style={{ background: 'white', borderRadius: '10px', padding: '1rem', marginBottom: '1.25rem', boxShadow: '0 2px 8px rgba(10,41,71,0.08)', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <input
            style={{ ...cellInput, flex: 1, padding: '8px 12px', fontSize: '0.875rem' }}
            placeholder="Category name (e.g. Data Analytics)"
            value={newCatName}
            onChange={e => setNewCatName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addCategory()}
            autoFocus
          />
          <button style={btnPrimary} onClick={addCategory}>Add</button>
        </div>
      )}

      {/* Category sections */}
      {categories.map(cat => {
        const isCollapsed = !!collapsed[cat.id];
        const catData     = data[cat.id] || {};
        const companies   = catData.companies || {};
        const timeline    = catData.timeline  || [];
        const catFilled   = companyColumns.filter(co => companies[co]?.vendor?.trim()).length;
        const doneCount   = timeline.filter(t => t.status === 'Complete').length;

        return (
          <div key={cat.id} style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 10px rgba(10,41,71,0.09)', overflow: 'hidden', marginBottom: '0.875rem' }}>

            {/* Header */}
            <div
              style={{ background: brand.deepBlue, borderBottom: isCollapsed ? 'none' : `3px solid ${brand.goldenrod}`, padding: '0.8rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', userSelect: 'none' }}
              onClick={() => toggleCollapse(cat.id)}
            >
              <i className={`bi bi-${cat.icon}`} style={{ color: brand.goldenrod, fontSize: '0.9rem', flexShrink: 0 }} />
              {editingCategoryId === cat.id ? (
                <input
                  autoFocus
                  value={editingCategoryName}
                  onClick={e => e.stopPropagation()}
                  onChange={e => setEditingCategoryName(e.target.value)}
                  onBlur={saveRenameCategory}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); saveRenameCategory(); }
                    if (e.key === 'Escape') { e.preventDefault(); cancelRenameCategory(); }
                  }}
                  style={{
                    background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: '4px', color: 'white', padding: '3px 8px', fontSize: '0.83rem',
                    fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px',
                    outline: 'none', fontFamily: 'inherit', minWidth: '290px',
                  }}
                />
              ) : (
                <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.83rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0, flexShrink: 0, minWidth: '290px' }}>
                  {cat.name}
                </h5>
              )}
              <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                <button
                  type="button"
                  title="Rename category"
                  onClick={() => startRenameCategory(cat)}
                  style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', padding: '2px 4px', fontSize: '0.85rem', lineHeight: 1 }}
                >
                  <i className="bi bi-pencil" />
                </button>
                <button
                  type="button"
                  title="Hide category"
                  onClick={() => removeCategory(cat)}
                  style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', padding: '2px 4px', fontSize: '0.85rem', lineHeight: 1 }}
                >
                  <i className="bi bi-trash" />
                </button>
              </div>

              {/* Selected vendor — stopPropagation prevents header collapse on click */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '0.25rem' }} onClick={e => e.stopPropagation()}>
                <span style={{ color: brand.goldenrod, fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                  Target:
                </span>
                <input
                  value={catData.selectedVendor || ''}
                  onChange={e => updateMeta(cat.id, 'selectedVendor', e.target.value)}
                  onBlur={e => saveSelectedVendor(cat.id, e.target.value)}
                  placeholder="Choose vendor…"
                  style={{
                    background:  catData.selectedVendor ? 'rgba(208,155,44,0.2)' : 'rgba(255,255,255,0.1)',
                    border:      catData.selectedVendor ? `1px solid ${brand.goldenrod}` : '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '4px',
                    color:       catData.selectedVendor ? brand.goldenrod : 'rgba(255,255,255,0.5)',
                    padding:     '3px 8px', fontSize: '0.8rem', outline: 'none',
                    fontFamily:  'inherit', fontWeight: catData.selectedVendor ? 700 : 400,
                    width:       '160px',
                  }}
                />
              </div>

              <div style={{ flex: 1 }} />

              {timeline.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'rgba(255,255,255,0.1)', borderRadius: '12px', padding: '2px 8px' }}>
                  <i className="bi bi-calendar3" style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem' }} />
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem' }}>{doneCount}/{timeline.length} tasks</span>
                </div>
              )}

              <div style={{ display: 'flex', gap: '3px' }}>
                {companyColumns.map(co => {
                  const entry = companies[co] || {};
                  const color = FIRM_BOX_COLORS[normalizeAuditStatus(entry.status)];
                  return (
                    <div key={co} title={`${co}: ${entry.status || 'TBD'}${entry.vendor ? ` — ${entry.vendor}` : ''}`} style={{
                      width: '20px', height: '20px', borderRadius: '3px', fontSize: '0.6rem', fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      background: color || 'rgba(255,255,255,0.12)',
                      color:      color ? 'white' : 'rgba(255,255,255,0.35)',
                    }}>
                      {co.charAt(0)}
                    </div>
                  );
                })}
              </div>

              <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.7rem', flexShrink: 0 }}>{catFilled}/{companyColumns.length}</span>
              <i className={`bi bi-chevron-${isCollapsed ? 'down' : 'up'}`} style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.7rem', flexShrink: 0 }} />
            </div>

            {/* Expanded body */}
            {!isCollapsed && (
              <>
                {/* Audit table */}
                <div style={{ borderBottom: `1px solid ${brand.softGray}` }}>
                  <div style={{ padding: '0.6rem 1.25rem', background: 'rgba(10,41,71,0.04)', display: 'flex', alignItems: 'center', gap: '0.5rem', borderBottom: `1px solid ${brand.softGray}` }}>
                    <i className="bi bi-table" style={{ color: brand.tealBlue, fontSize: '0.8rem' }} />
                    <span style={{ fontWeight: 700, fontSize: '0.75rem', color: brand.deepBlue, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Current Solutions by Company</span>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
                      <thead>
                        <tr>
                          <th style={{ ...thStyle, width: '80px' }}>Company</th>
                          <th style={{ ...thStyle, minWidth: '160px' }}>Solution / Vendor</th>
                          <th style={{ ...thStyle, width: '110px' }}>Status</th>
                          <th style={{ ...thStyle, width: '110px' }}>Monthly Cost</th>
                          <th style={{ ...thStyle, width: '135px' }}>Contract Renewal</th>
                          <th style={{ ...thStyle, width: '145px' }}>Contact</th>
                          <th style={{ ...thStyle, minWidth: '170px' }}>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {companyColumns.map((co, idx) => {
                          const entry = companies[co] || blankEntry();
                          return (
                            <tr key={co} style={{ background: idx % 2 === 1 ? 'rgba(10,41,71,0.02)' : 'white', borderBottom: `1px solid ${brand.softGray}` }}>
                              <td style={{ padding: '0.4rem 0.75rem', whiteSpace: 'nowrap' }}>
                                <span style={{
                                  display: 'inline-block', borderRadius: '4px', padding: '2px 8px',
                                  fontWeight: 700, fontSize: '0.78rem',
                                  background: entry.vendor?.trim() ? `${AUDIT_STATUSES[entry.status]}22` : 'rgba(10,41,71,0.07)',
                                  color:      entry.vendor?.trim() ? AUDIT_STATUSES[entry.status]       : brand.charcoal,
                                  border:     entry.vendor?.trim() ? `1px solid ${AUDIT_STATUSES[entry.status]}55` : 'none',
                                }}>
                                  {co}
                                </span>
                              </td>
                              <td style={{ padding: '0.35rem 0.75rem' }}>
                                <input style={cellInput} value={entry.vendor} placeholder="Enter solution…"
                                  onChange={e => updateCompany(cat.id, co, 'vendor', e.target.value)}
                                  onBlur={() => saveEntry(cat.id, co)} />
                              </td>
                              <td style={{ padding: '0.35rem 0.75rem' }}>
                                <select value={entry.status}
                                  onChange={e => handleStatusChange(cat.id, co, e.target.value)}
                                  style={{ ...cellInput, fontWeight: 700, color: AUDIT_STATUSES[entry.status], cursor: 'pointer' }}>
                                  {Object.keys(AUDIT_STATUSES).map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                              </td>
                              <td style={{ padding: '0.35rem 0.75rem' }}>
                                <input style={cellInput} value={entry.cost} placeholder="$0 / mo"
                                  onChange={e => updateCompany(cat.id, co, 'cost', e.target.value)}
                                  onBlur={() => saveEntry(cat.id, co)} />
                              </td>
                              <td style={{ padding: '0.35rem 0.75rem' }}>
                                <input type="date" style={cellInput} value={entry.renewal}
                                  onChange={e => updateCompany(cat.id, co, 'renewal', e.target.value)}
                                  onBlur={() => saveEntry(cat.id, co)} />
                              </td>
                              <td style={{ padding: '0.35rem 0.75rem' }}>
                                <input style={cellInput} value={entry.contact} placeholder="Name / email"
                                  onChange={e => updateCompany(cat.id, co, 'contact', e.target.value)}
                                  onBlur={() => saveEntry(cat.id, co)} />
                              </td>
                              <td style={{ padding: '0.35rem 0.75rem' }}>
                                <NotesCell value={entry.notes}
                                  onChange={v => updateCompany(cat.id, co, 'notes', v)}
                                  onSave={() => saveEntry(cat.id, co)} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Timeline */}
                <div>
                  <div style={{ padding: '0.6rem 1.25rem', background: 'rgba(10,41,71,0.04)', display: 'flex', alignItems: 'center', gap: '0.5rem', borderBottom: `1px solid ${brand.softGray}` }}>
                    <i className="bi bi-calendar2-range" style={{ color: brand.tealBlue, fontSize: '0.8rem' }} />
                    <span style={{ fontWeight: 700, fontSize: '0.75rem', color: brand.deepBlue, textTransform: 'uppercase', letterSpacing: '0.5px', flex: 1 }}>Migration & Training Timeline</span>
                    <button style={btnSmall} onClick={() => addTask(cat.id)}>
                      <i className="bi bi-plus me-1" />Add Task
                    </button>
                  </div>

                  <div style={{ padding: '0.75rem 1.25rem' }}>
                    {timeline.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '1.5rem', color: brand.charcoal, fontSize: '0.85rem' }}>
                        <i className="bi bi-calendar-plus" style={{ fontSize: '1.5rem', color: brand.softGray, display: 'block', marginBottom: '0.5rem' }} />
                        No timeline tasks yet. Click <strong>Add Task</strong> to plan the migration.
                      </div>
                    ) : (
                      <>
                        <GanttChart tasks={timeline} />
                        <div style={{ overflowX: 'auto', marginTop: '0.75rem' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '860px' }}>
                            <thead>
                              <tr>
                                <th style={{ ...thStyle, minWidth: '160px' }}>Task</th>
                                <th style={{ ...thStyle, width: '120px' }}>Phase</th>
                                <th style={{ ...thStyle, width: '120px' }}>Owner</th>
                                <th style={{ ...thStyle, width: '125px' }}>Start</th>
                                <th style={{ ...thStyle, width: '125px' }}>End</th>
                                <th style={{ ...thStyle, width: '115px' }}>Status</th>
                                <th style={{ ...thStyle, minWidth: '160px' }}>Notes</th>
                                <th style={{ ...thStyle, width: '50px' }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {timeline.map((task, idx) => (
                                <tr key={task.id} style={{ background: idx % 2 === 1 ? 'rgba(10,41,71,0.02)' : 'white', borderBottom: `1px solid ${brand.softGray}` }}>
                                  <td style={{ padding: '0.35rem 0.75rem' }}>
                                    <input style={cellInput} value={task.task} placeholder="Task description…"
                                      onChange={e => updateTask(cat.id, task.id, 'task', e.target.value)}
                                      onBlur={() => saveTask(cat.id, task.id)} />
                                  </td>
                                  <td style={{ padding: '0.35rem 0.75rem' }}>
                                    <select style={cellInput} value={task.type}
                                      onChange={e => { updateTask(cat.id, task.id, 'type', e.target.value); saveTask(cat.id, task.id); }}>
                                      {TIMELINE_PHASES.map(p => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                  </td>
                                  <td style={{ padding: '0.35rem 0.75rem' }}>
                                    <input style={cellInput} value={task.owner} placeholder="Owner"
                                      onChange={e => updateTask(cat.id, task.id, 'owner', e.target.value)}
                                      onBlur={() => saveTask(cat.id, task.id)} />
                                  </td>
                                  <td style={{ padding: '0.35rem 0.75rem' }}>
                                    <input type="date" style={cellInput} value={task.startDate}
                                      onChange={e => updateTask(cat.id, task.id, 'startDate', e.target.value)}
                                      onBlur={() => saveTask(cat.id, task.id)} />
                                  </td>
                                  <td style={{ padding: '0.35rem 0.75rem' }}>
                                    <input type="date" style={cellInput} value={task.endDate}
                                      onChange={e => updateTask(cat.id, task.id, 'endDate', e.target.value)}
                                      onBlur={() => saveTask(cat.id, task.id)} />
                                  </td>
                                  <td style={{ padding: '0.35rem 0.75rem' }}>
                                    <select value={task.status}
                                      onChange={e => handleTaskStatusChange(cat.id, task.id, e.target.value)}
                                      style={{ ...cellInput, fontWeight: 700, color: TIMELINE_STATUSES[task.status], cursor: 'pointer' }}>
                                      {Object.keys(TIMELINE_STATUSES).map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                  </td>
                                  <td style={{ padding: '0.35rem 0.75rem' }}>
                                    <NotesCell value={task.notes}
                                      onChange={v => updateTask(cat.id, task.id, 'notes', v)}
                                      onSave={() => saveTask(cat.id, task.id)} />
                                  </td>
                                  <td style={{ padding: '0.35rem 0.75rem', textAlign: 'center' }}>
                                    <button style={btnDanger} onClick={() => removeTask(cat.id, task.id)}>✕</button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        );
      })}

      {/* Firms — shared lookup of the firms being consolidated. Editable here
          and on the HR Config page; both are gated to HR or TECH. */}
      <div style={{ marginTop: '1.5rem' }}>
        <FirmsSection />
      </div>
    </div>
  );
}
